import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { sendPasswordReset } from '../../../lib/emailService';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!email) {
      setError('Email is required');
      return;
    }

    if (!/\S+@\S+\.\S+/?.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    // Trigger Supabase native password reset (sends the actual reset link)
    const { error: resetError } = await resetPassword(email);

    if (resetError) {
      setError(resetError?.message || 'Failed to send reset email. Please try again.');
      setLoading(false);
      return;
    }

    // Also send branded Resend email with the reset link
    await sendPasswordReset(
      email,
      email?.split('@')?.[0],
      `${window.location?.origin}/login-screen`
    );

    setSuccess(true);
    setLoading(false);
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMessage('');

    const { error: resetError } = await resetPassword(email);

    if (resetError) {
      setResendMessage('Failed to resend. Please try again.');
      setResendLoading(false);
      return;
    }

    await sendPasswordReset(
      email,
      email?.split('@')?.[0],
      `${window.location?.origin}/login-screen`
    );

    setResendMessage('Reset link resent successfully!');
    setResendLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    setResendMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-md bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              Reset Password
            </h2>
            <button
              onClick={handleClose}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm text-foreground">
                  Enter your email address and we will send you a link to reset your password.
                </p>
              </div>

              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e?.target?.value);
                  setError('');
                }}
                error={error}
                required
              />

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  loading={loading}
                  iconName="Send"
                  iconPosition="right"
                >
                  Send Reset Link
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Check Your Email
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                We've sent a password reset link to
              </p>
              <p className="text-sm font-semibold text-primary mb-4 break-all">
                {email}
              </p>
              <div className="p-3 rounded-lg bg-muted/50 border border-border mb-5 text-left">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Didn't receive the email? Check your spam folder or click below to resend.
                </p>
              </div>

              {resendMessage && (
                <div className={`p-3 rounded-lg mb-4 text-sm ${
                  resendMessage?.includes('success')
                    ? 'bg-success/10 border border-success/20 text-success' :'bg-error/10 border border-error/20 text-error'
                }`}>
                  {resendMessage}
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  loading={resendLoading}
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={handleResend}
                >
                  Resend Email
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleClose}
                >
                  Got It
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;